import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, X, AlertCircle, CheckCircle2, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { agentFetch } from '@/lib/migrationChat';
import type { CoaAccount, ImportProposal, BalancesProposal, ReadinessCheck } from '@/lib/migrationChat';
import { sliceTable, toWireRows, type ParsedFile } from '@/lib/parseSpreadsheet';

/**
 * Confirmation modals for the chat-driven migration wizard. The LLM only
 * *proposes*; these modals visualize the proposal and, on confirm, execute via
 * the existing REST endpoints. Each calls onResult(text) afterwards so the
 * caller can feed the outcome back into the chat for the assistant to continue.
 */

interface RowResult { row_no: number; status: 'ok' | 'error'; name?: string; message?: string }
interface ImportJob { total_rows: number; success_rows: number; error_rows: number }
interface ImportResp { job: ImportJob; results: RowResult[] }

const COA_FIELDS = ['account_number', 'name', 'root_type', 'account_type', 'account_currency', 'is_group', 'parent_account_number', 'parent_account_name'];

/* ─────────── COA proposal ─────────── */

export function CoaProposalModal({
  accounts, sessionId, onResult, onClose,
}: {
  accounts: CoaAccount[];
  sessionId: string;
  onResult: (text: string) => void;
  onClose: () => void;
}) {
  const sorted = useMemo(
    () => [...accounts].sort((a, b) => a.account_number.localeCompare(b.account_number)),
    [accounts],
  );

  // Idempotent creation: fetch the accounts that already exist and only create
  // the ones that are missing. Re-running therefore converges to a complete
  // chart instead of erroring on duplicate account numbers — no manual delete.
  const existing = useQuery({
    queryKey: ['coa-existing', sessionId],
    queryFn: () => api<{ items?: Array<{ account_number?: string; name?: string }> }>('/accounting/accounts?limit=1000'),
  });
  // An account is "already present" if its number OR its name matches an
  // existing one — the account name is unique per company, so name collisions
  // error just like number collisions. Skip both to stay idempotent.
  const haveNum = useMemo(
    () => new Set((existing.data?.items ?? []).map((a) => a.account_number).filter(Boolean) as string[]),
    [existing.data],
  );
  const haveName = useMemo(
    () => new Set((existing.data?.items ?? []).map((a) => (a.name ?? '').trim().toLowerCase()).filter(Boolean)),
    [existing.data],
  );
  const exists = (a: CoaAccount) => haveNum.has(a.account_number) || haveName.has(a.name.trim().toLowerCase());
  const toCreate = useMemo(() => sorted.filter((a) => !exists(a)), [sorted, haveNum, haveName]); // eslint-disable-line react-hooks/exhaustive-deps
  const skipped = sorted.length - toCreate.length;
  // Proposal account_number → name, so we can pass a parent-by-name fallback
  // for children whose parent already exists unnumbered (e.g. a seeded chart).
  const nameByNumber = useMemo(() => new Map(sorted.map((a) => [a.account_number, a.name])), [sorted]);

  const create = useMutation({
    mutationFn: async () => {
      let created = 0;
      let errors = 0;
      if (toCreate.length > 0) {
        const mapping = Object.fromEntries(COA_FIELDS.map((f) => [f, f]));
        const rows = [
          COA_FIELDS,
          ...toCreate.map((a) => [
            a.account_number, a.name, a.root_type, a.account_type ?? '',
            a.account_currency ?? '', a.is_group ? 'true' : 'false', a.parent_account_number ?? '',
            a.parent_account_number ? (nameByNumber.get(a.parent_account_number) ?? '') : '',
          ]),
        ];
        const res = await api<ImportResp>('/admin/imports/commit', {
          method: 'POST', body: { doctype: 'account', mapping, rows },
        });
        created = res.job.success_rows;
        errors = res.job.error_rows;
      }
      await agentFetch(`/api/agent/v1/migration/${sessionId}/complete-step`, {
        method: 'POST', body: { step: 'coa' },
      });
      return { created, errors, skipped };
    },
    onSuccess: (r) => {
      const parts = [`${r.created} akun dibuat`];
      if (r.skipped) parts.push(`${r.skipped} sudah ada (dilewati)`);
      if (r.errors) parts.push(`${r.errors} error`);
      onResult(`Chart of accounts: ${parts.join(', ')}.`);
    },
  });

  const done = create.data;

  return (
    <ModalShell title="Chart of Accounts" subtitle={`${accounts.length} accounts proposed (PSAK-aligned)`} onClose={onClose}>
      {!done ? (
        <>
          <div className="mb-3 text-caption text-stone">
            {existing.isLoading
              ? 'Mengecek akun yang sudah ada…'
              : skipped > 0
                ? `${toCreate.length} akun baru akan dibuat; ${skipped} sudah ada di sistem dan dilewati.`
                : `${toCreate.length} akun akan dibuat.`}
          </div>
          <div className="max-h-[46vh] overflow-y-auto -mx-1">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone sticky top-0">
                <tr>
                  <th className="text-left font-medium px-3 py-2">No.</th>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Root</th>
                  <th className="text-left font-medium px-3 py-2">Type</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => {
                  const present = exists(a);
                  return (
                    <tr key={a.account_number} className={cn('border-t border-hairline', present && 'opacity-45')}>
                      <td className="px-3 py-1.5 font-mono text-caption text-charcoal">{a.account_number}</td>
                      <td className={cn('px-3 py-1.5', a.is_group && 'font-semibold text-ink')}>
                        {a.name}
                        {present && <span className="ml-2 text-caption text-stone">· sudah ada</span>}
                      </td>
                      <td className="px-3 py-1.5 text-charcoal">{a.root_type}</td>
                      <td className="px-3 py-1.5 text-stone">{a.account_type ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(create.error || existing.error) && <ErrLine>{((create.error ?? existing.error) as Error).message}</ErrLine>}
          <ModalFooter>
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={existing.isLoading}>
              {toCreate.length > 0 ? <>Buat {toCreate.length} akun <ChevronRight className="size-3.5" /></> : 'Tandai selesai'}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <DonePanel
          okCount={done.created}
          errCount={done.errors}
          skipped={done.skipped}
          label="akun dibuat"
          onClose={onClose}
        />
      )}
    </ModalShell>
  );
}

/* ─────────── Data import proposal ─────────── */

export function ImportProposalModal({
  proposal, file, sessionId, onResult, onClose,
}: {
  proposal: ImportProposal;
  file: ParsedFile | undefined;
  sessionId: string;
  onResult: (text: string) => void;
  onClose: () => void;
}) {
  const sliced = useMemo(
    () => (file ? sliceTable(file.grid, proposal.header_row, proposal.data_start_row) : null),
    [file, proposal.header_row, proposal.data_start_row],
  );
  const wireRows = useMemo(() => (sliced ? toWireRows(sliced) : []), [sliced]);
  const dataCount = sliced ? sliced.dataRows.length : 0;

  // Live preview (dry run) as soon as the modal opens.
  const preview = useMutation({
    mutationFn: () => api<ImportResp>('/admin/imports/preview', {
      method: 'POST', body: { doctype: proposal.doctype, mapping: proposal.mapping, rows: wireRows },
    }),
  });
  useEffect(() => { if (file) preview.mutate(); /* eslint-disable-next-line */ }, [file]);

  const commit = useMutation({
    mutationFn: async () => {
      const res = await api<ImportResp>('/admin/imports/commit', {
        method: 'POST', body: { doctype: proposal.doctype, mapping: proposal.mapping, rows: wireRows },
      });
      await agentFetch(`/api/agent/v1/migration/${sessionId}/complete-step`, {
        method: 'POST', body: { step: 'data_migration' },
      });
      return res;
    },
    onSuccess: (r) => {
      onResult(`Imported "${proposal.doctype}": ${r.job.success_rows} rows ok, ${r.job.error_rows} errors.`);
    },
  });

  const report = commit.data ?? preview.data;
  const committed = !!commit.data;

  if (!file) {
    return (
      <ModalShell title="Import data" subtitle={proposal.doctype} onClose={onClose}>
        <ErrLine>Attached file is no longer available in this session. Please drop the file again.</ErrLine>
        <ModalFooter><Button onClick={onClose}>Close</Button></ModalFooter>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={`Import → ${proposal.doctype}`}
      subtitle={`${file.filename} · ${dataCount} data rows · header on row ${proposal.header_row + 1}`}
      onClose={onClose}
    >
      {proposal.notes && <p className="text-caption text-stone mb-3">{proposal.notes}</p>}

      <div className="text-caption text-stone font-semibold mb-1">Column mapping</div>
      <div className="rounded-md border border-hairline divide-y divide-hairline mb-4 max-h-[24vh] overflow-y-auto">
        {Object.entries(proposal.mapping).map(([col, field]) => (
          <div key={col} className="flex items-center gap-2 px-3 py-1.5 text-body-sm">
            <span className="font-mono text-charcoal truncate flex-1">{col}</span>
            <ChevronRight className="size-3.5 text-stone shrink-0" />
            <span className="text-ink truncate flex-1">{field}</span>
          </div>
        ))}
      </div>

      {(preview.isPending && !report) && <p className="text-caption text-stone">Validating rows…</p>}
      {report && (
        <Report report={report} committed={committed} />
      )}
      {(preview.error || commit.error) && (
        <ErrLine>{((commit.error ?? preview.error) as Error).message}</ErrLine>
      )}

      {!committed ? (
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} disabled={commit.isPending}>Cancel</Button>
          <Button onClick={() => commit.mutate()} loading={commit.isPending} disabled={!report}>
            Import {report ? report.job.success_rows : dataCount} rows <ChevronRight className="size-3.5" />
          </Button>
        </ModalFooter>
      ) : (
        <ModalFooter><Button onClick={onClose}>Done</Button></ModalFooter>
      )}
    </ModalShell>
  );
}

/* ─────────── Opening balances proposal ─────────── */

interface OpeningBalanceProposalResp {
  lines: Array<{ account_number: string; resolved_account_name?: string; debit: string; credit: string }>;
  total_debit: string;
  total_credit: string;
  balanced: boolean;
  imbalance?: string;
  unmapped_accounts?: string[];
  stock_check?: { matches: boolean; trial_balance_debit: string; stock_ledger_total: string; difference?: string };
  posting_date: string;
}

export function BalancesProposalModal({
  proposal, file, sessionId, onResult, onClose,
}: {
  proposal: BalancesProposal;
  file: ParsedFile | undefined;
  sessionId: string;
  onResult: (text: string) => void;
  onClose: () => void;
}) {
  const lines = useMemo(() => {
    if (!file) return [];
    const t = sliceTable(file.grid, proposal.header_row, proposal.data_start_row);
    const idx = (name?: string) => (name ? t.headers.indexOf(name) : -1);
    const ai = idx(proposal.account_number_column);
    const li = idx(proposal.label_column);
    const di = idx(proposal.debit_column);
    const ci = idx(proposal.credit_column);
    return t.dataRows
      .map((r) => ({
        account_number: ai >= 0 ? r[ai] ?? '' : '',
        legacy_label: li >= 0 ? r[li] ?? '' : '',
        debit: di >= 0 ? r[di] ?? '' : '',
        credit: ci >= 0 ? r[ci] ?? '' : '',
      }))
      .filter((l) => l.account_number || l.debit || l.credit);
  }, [file, proposal]);

  const propose = useMutation({
    mutationFn: () => agentFetch<OpeningBalanceProposalResp>(
      `/api/agent/v1/migration/${sessionId}/opening-balances/propose`,
      { method: 'POST', body: { lines, posting_date: proposal.posting_date || undefined } },
    ),
  });
  useEffect(() => { if (file && lines.length) propose.mutate(); /* eslint-disable-next-line */ }, [file]);

  const submit = useMutation({
    mutationFn: () => agentFetch<{ journal_entry_id: string }>(
      `/api/agent/v1/migration/${sessionId}/opening-balances/submit`, { method: 'POST' },
    ),
    onSuccess: (r) => onResult(`Opening balances posted — journal entry ${r.journal_entry_id}.`),
  });

  const p = propose.data;
  const submitted = submit.data;
  const blockers = p ? (!p.balanced || (p.unmapped_accounts?.length ?? 0) > 0 || (p.stock_check && !p.stock_check.matches)) : true;

  if (!file) {
    return (
      <ModalShell title="Opening balances" onClose={onClose}>
        <ErrLine>Attached file is no longer available in this session. Please drop the trial balance again.</ErrLine>
        <ModalFooter><Button onClick={onClose}>Close</Button></ModalFooter>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title="Opening balances"
      subtitle={`${file.filename} · ${lines.length} lines`}
      onClose={onClose}
    >
      {proposal.notes && <p className="text-caption text-stone mb-3">{proposal.notes}</p>}
      {propose.isPending && <p className="text-caption text-stone">Reconciling…</p>}
      {propose.error && <ErrLine>{(propose.error as Error).message}</ErrLine>}

      {p && (
        <>
          <div className={cn('rounded-md border-l-4 p-3 mb-3', p.balanced ? 'border-l-brand-success bg-brand-success/5' : 'border-l-warning bg-warning/5')}>
            <div className="text-body-sm text-ink font-medium">
              {p.balanced ? '✓ Trial balance is balanced' : '⚠ Trial balance is not balanced'}
            </div>
            <div className="text-caption text-stone mt-0.5">
              Debit {p.total_debit} · Credit {p.total_credit}{p.imbalance ? ` · Imbalance ${p.imbalance}` : ''}
            </div>
          </div>

          {(p.unmapped_accounts?.length ?? 0) > 0 && (
            <div className="text-caption text-brand-error mb-3">
              {p.unmapped_accounts!.length} account(s) not in COA: <span className="font-mono">{p.unmapped_accounts!.join(', ')}</span>
            </div>
          )}
          {p.stock_check && (
            <div className={cn('text-caption mb-3', p.stock_check.matches ? 'text-stone' : 'text-brand-error')}>
              Stock reconciliation: trial balance {p.stock_check.trial_balance_debit} vs stock ledger {p.stock_check.stock_ledger_total}
              {p.stock_check.matches ? ' ✓' : ` (diff ${p.stock_check.difference})`}
            </div>
          )}

          <div className="max-h-[34vh] overflow-y-auto rounded-md border border-hairline">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone sticky top-0">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Account</th>
                  <th className="text-right font-medium px-3 py-2">Debit</th>
                  <th className="text-right font-medium px-3 py-2">Credit</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l, i) => (
                  <tr key={i} className="border-t border-hairline">
                    <td className="px-3 py-1.5"><span className="font-mono text-caption text-charcoal">{l.account_number}</span> {l.resolved_account_name ?? ''}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.debit || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.credit || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {submit.error && <ErrLine>{(submit.error as Error).message}</ErrLine>}

      {!submitted ? (
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>Cancel</Button>
          <Button onClick={() => submit.mutate()} loading={submit.isPending} disabled={blockers}>
            Post opening JE <ChevronRight className="size-3.5" />
          </Button>
        </ModalFooter>
      ) : (
        <ModalFooter><Button onClick={onClose}>Done</Button></ModalFooter>
      )}
    </ModalShell>
  );
}

/* ─────────── Readiness ─────────── */

export function ReadinessModal({ items, onClose }: { items: ReadinessCheck[]; onClose: () => void }) {
  const failing = items.filter((i) => !i.pass);
  const ready = items.length > 0 && failing.length === 0;
  return (
    <ModalShell
      title="Go-live readiness"
      subtitle={`${items.length - failing.length} of ${items.length} checks passing`}
      onClose={onClose}
    >
      <div className={cn('rounded-md border-l-4 p-3 mb-3', ready ? 'border-l-brand-success bg-brand-success/5' : 'border-l-warning bg-warning/5')}>
        <div className="text-body-sm text-ink font-medium">{ready ? '✅ Ready to go live' : `${failing.length} items need attention`}</div>
      </div>
      <ul className="space-y-1.5 max-h-[44vh] overflow-y-auto">
        {items.map((c) => (
          <li key={c.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-canvas border border-hairline">
            <span className={cn('size-5 rounded-full inline-flex items-center justify-center', c.pass ? 'bg-brand-success/15 text-brand-success' : 'bg-warning/15 text-warning')}>
              {c.pass ? <Check className="size-3" /> : <AlertCircle className="size-3" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-body-sm text-ink">{c.label}</div>
              {c.detail && <div className="text-caption text-stone">{c.detail}</div>}
            </div>
            {!c.pass && c.fix_url && <a href={c.fix_url} className="text-caption text-accent hover:underline">Fix →</a>}
          </li>
        ))}
      </ul>
      <ModalFooter><Button onClick={onClose}>Close</Button></ModalFooter>
    </ModalShell>
  );
}

/* ─────────── shared bits ─────────── */

function ModalShell({
  title, subtitle, onClose, children,
}: {
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogTitle><FileSpreadsheet className="size-4 inline mr-1.5 text-accent" />{title}</DialogTitle>
        {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        {children}
      </DialogContent>
    </Dialog>
  );
}

function ModalFooter({ children }: { children: React.ReactNode }) {
  return <div className="mt-5 flex items-center justify-end gap-2">{children}</div>;
}

function ErrLine({ children }: { children: React.ReactNode }) {
  return <div className="mt-3 text-caption text-brand-error">{children}</div>;
}

function Report({ report, committed }: { report: ImportResp; committed: boolean }) {
  const errors = report.results.filter((r) => r.status === 'error');
  return (
    <div className="mb-2">
      <div className="text-caption text-stone mb-1">
        {committed ? 'Committed' : 'Preview (dry run)'}: {report.job.success_rows} ok · {report.job.error_rows} errors
      </div>
      {errors.length > 0 && (
        <div className="max-h-[24vh] overflow-y-auto rounded-md border border-hairline">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone sticky top-0">
              <tr><th className="text-left font-medium px-3 py-1.5">Row</th><th className="text-left font-medium px-3 py-1.5">Error</th></tr>
            </thead>
            <tbody>
              {errors.map((r) => (
                <tr key={r.row_no} className="border-t border-hairline">
                  <td className="px-3 py-1.5 font-mono text-caption text-stone">{r.row_no}</td>
                  <td className="px-3 py-1.5 text-brand-error inline-flex items-center gap-1"><X className="size-3 shrink-0" />{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DonePanel({ okCount, errCount, skipped, label, onClose }: { okCount: number; errCount: number; skipped?: number; label: string; onClose: () => void }) {
  return (
    <div className="text-center py-6">
      <CheckCircle2 className="size-10 text-brand-success mx-auto mb-2" />
      <div className="text-body text-ink">
        {okCount} {label}{skipped ? `, ${skipped} sudah ada` : ''}{errCount ? `, ${errCount} error` : ''}.
      </div>
      <div className="mt-4"><Button onClick={onClose}>Done</Button></div>
    </div>
  );
}
